/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getCatalogId, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    CommerceProperty,
    ProductCatalog,
    ProductDeliveryOptions,
    ProductDimensionValue,
    ProductDimensionValueInventoryAvailability,
    ProductPrice,
    ProductsDataActions,
    ProductWarehouseInventoryInformation,
    ReleasedProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions,
    DeliveryMode,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant,
    getProductAvailabilitiesForSelectedVariant,
    getSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    IProductInventoryInformation,
    IPromiseQueue,
    PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput,
    mapProductInventoryInformation,
    createInventoryAvailabilitySearchCriteria,
    getDimensionValuesFromQuery,
    setDimensionValuesToQuery
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    updateMaxQuantityForCartLineItem
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    getBuyBoxInventoryLabel,
    getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate,
    getBuyboxProductAddToWishlist,
    getBuyboxProductComparisonButton,
    getBuyboxProductDescription,
    getBuyboxProductQuantity,
    getBuyboxProductRating,
    getBuyboxProductTitle,
    getBuyboxProductUnitOfMeasure,
    getBuyboxShopSimilarButton,
    getQuantityLimitsMessages,
    RetailDefaultOrderQuantityLimitsFeatureName
} from '@msdyn365-commerce-modules/buybox';
import { getBuyboxProductConfigure } from './components/buybox-product-configure';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCommonData,
    IBuyboxExtentedProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IErrorState,
    ShopSimiliarButtonType
} from '@msdyn365-commerce-modules/buybox';
import { IHeiBuyboxCallbacks, IHeiBuyboxState, IHeiBuyboxV2Data } from './hei-buybox-v2.data';
import { IHeiBuyboxV2Props, IHeiBuyboxV2Resources } from './hei-buybox-v2.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/hei-buybox-v2-find-in-store';
import { RecentlyViewedState } from '@msdyn365-commerce-modules/buybox';
import getProductPromotionsAction, { GetProductPromotionsInput } from '../../actions/get-product-promotions.action';
import { getApplicableWarrantiesAction, getApplicableWarrantiesInput } from '../../actions/get-applicable-warranties.action';
import { getBuyboxAddToCart } from './common/buybox-components';
import { IGetProductPromotionResponse } from '../../actions/DataServiceEntities.g';
import getMediaLocationsForSelectedVariantActionDataAction, {
    MediaLocationsForSelectedVariantInput
} from '../../actions/get-media-locations-for-selected-variant.action';
import { getBuyboxProductPrice } from './components/getBuyboxProductPrice';

export interface IBuyboxViewProps extends IHeiBuyboxV2Props<IHeiBuyboxV2Data> {
    state: IHeiBuyboxState;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IHeiBuyboxCallbacks;
    mediaGallery?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    max: number | undefined;
    addToCart: IBuyboxAddToCartViewProps;
    findInStore?: IBuyboxFindInStoreViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    catalogs?: ProductCatalog[] | undefined;
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<IHeiBuyboxV2Props<IHeiBuyboxV2Data>, IHeiBuyboxState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};

    private readonly buyboxCallbacks: IHeiBuyboxCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            const selecedDimensions: Record<number, string> = {};
            selecedDimensions[selectedDimensionId] = selectedDimensionValueId;
            return this.buyboxCallbacks.dimensionsSelectedAsync(selecedDimensions);
        },
        dimensionsSelectedAsync: async (selecedDimensions: Record<number, string>): Promise<void> => {
            Object.keys(selecedDimensions).forEach(
                selectedDimensionId => (this.dimensions[+selectedDimensionId] = selecedDimensions[+selectedDimensionId])
            );

            process.env.NODE_ENV === 'development' && console.log('Buybox', 'dimensionsSelectedAsync', selecedDimensions, this.dimensions);
            return this.dimensionUpdateQueue
                .enqueue(async () => {
                    return this._updateDimensions();
                })
                .catch((error: unknown) => {
                    // Ignore discarded processes.
                    if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                        throw error;
                    }
                });
        },
        getDropdownName: (dimensionType: number, resources: IHeiBuyboxV2Resources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        },

        setSelectedWarranty: (warranty: SimpleProduct | null): void => {
            this.setState({ selectedWarranty: warranty });
        }
    };

    private readonly telemetryContent: ITelemetryContent;

    private readonly mountCategories: { [category: string]: boolean } = {};
    private readonly packSizeCategories: { [category: string]: boolean } = {};

    public constructor(props: IHeiBuyboxV2Props<IHeiBuyboxV2Data>, state: IBuyboxState) {
        super(props);
        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: 1,
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,

            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false,
            selectedWarranty: null,
            productPromotions: undefined,
            storeAvailabilities: undefined,
            applicableWarranties: undefined,
            mediaLocations: undefined
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);

        if (props.config.mountCategories) {
            for (const c of props.config.mountCategories.split('|')) {
                this.mountCategories[c] = true;
            }
        }

        if (props.config.packSizeCategories === 'string') {
            for (const c of props.config.packSizeCategories.split('|')) {
                this.packSizeCategories[c] = true;
            }
        }
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPromotions,
                applicableWarranties,
                productPrice,
                allDimensions
            },
            context
        } = this.props;

        const matchedDimensions = getDimensionValuesFromQuery(context.request.url.requestUrl);
        await allDimensions.then(async productDimensionsData => {
            let dimensionUpdated = false;
            for (const productDimensionFull of productDimensionsData) {
                if (!ArrayExtensions.hasElements(productDimensionFull.DimensionValues)) {
                    continue;
                }

                const dropdownId = productDimensionFull.DimensionTypeValue.toString();
                const dimensionFromQuery = matchedDimensions.find(
                    dimensionFromQuery => dimensionFromQuery.DimensionTypeValue.toString() === dropdownId
                );

                // no dimension selected from URL
                if (!dimensionFromQuery) {
                    let autoSelectValue = productDimensionFull.dimensionValuesWithInventory?.find(
                        dvi => dvi.PhysicalAvailableInventoryLevelCode !== 'OOS'
                    )?.DimensionValue;

                    if (!autoSelectValue) {
                        // style or condition, try to select '9'
                        if (productDimensionFull.DimensionTypeValue === 4) {
                            autoSelectValue =
                                productDimensionFull.DimensionValues.find(dv => dv.Value === '9') ??
                                productDimensionFull.DimensionValues[0];
                        } else {
                            autoSelectValue = productDimensionFull.DimensionValues[0];
                        }
                    }

                    process.env.NODE_ENV === 'development' && console.log('Buybox dim', dropdownId, '=>', autoSelectValue.Value);

                    this.dimensions[productDimensionFull.DimensionTypeValue] = autoSelectValue.RecordId.toString();
                    dimensionUpdated = true;
                }
            }

            process.env.NODE_ENV === 'development' &&
                console.log('Buybox dim', 'matchedDimensions', matchedDimensions, this.dimensions, 'updated=', dimensionUpdated);

            if (dimensionUpdated) {
                process.env.NODE_ENV === 'development' && console.log('Buybox auto select dim', dimensionUpdated);
                await this._updateDimensions();
            }
        });

        await productPrice.then(async result => {
            this._updatePrice(result);
        });

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
            await this._updateQuantitiesInState(product);
            await this._updateAvailableForPickup(product);
        }

        if (product && context.app.config.maxRecentlyViewedItemsCount > 0) {
            RecentlyViewedState.instance(this.props.context, context.app.config.maxRecentlyViewedItemsCount, product.RecordId);
        }

        await productPromotions.then(async result => {
            this.setState({ productPromotions: result });
        });

        await applicableWarranties.then(async result => {
            this.setState({ applicableWarranties: result });
        });
    }

    public render(): JSX.Element | null {
        const {
            slots: { mediaGallery, productComparisonButton },
            data: {
                product: { result: product }
            },
            config: { className = '' }
        } = this.props;

        const { max } = this.state;

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }

        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const inventoryLevel = context.app.config.inventoryLevel;
        const dimensionValuesWithInventory = ArrayExtensions.flatten(
            ArrayExtensions.validValues(this.props.data.allDimensions.result?.map(value => value.dimensionValuesWithInventory))
        );
        const hasAvailableProducts =
            !ArrayExtensions.hasElements(dimensionValuesWithInventory) ||
            dimensionValuesWithInventory.some((value: unknown) => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(
                    value as ProductDimensionValueInventoryAvailability,
                    inventoryLevel
                );
                return inventoryLevelCode !== InventoryLevelValues.outOfStock;
            });
        const catalogId = getCatalogId(this.props.context.request);

        const hasSerialNumber = product?.Behavior?.HasSerialNumber || false;

        const viewProps: IBuyboxViewProps = {
            ...(this.props as IHeiBuyboxV2Props<IHeiBuyboxV2Data>),
            state: this.state,
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('ms-buybox', 'hei-buybox-v2', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery'
            },
            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            title: getBuyboxProductTitle(props),
            description: getBuyboxProductDescription(props),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            unitOfMeasure: getBuyboxProductUnitOfMeasure(props),
            configure: getBuyboxProductConfigure(
                props,
                this.state,
                this.buyboxCallbacks,
                undefined,
                undefined,
                this.props.data.allDimensions.result
            ),
            findInStore: getBuyboxFindInStore(this.props, this.state, this.buyboxCallbacks),
            price: getBuyboxProductPrice(this.props, this.state),
            addToCart: getBuyboxAddToCart(
                props,
                this.state,
                this.buyboxCallbacks,
                defaultMinimumKeyInPrice,
                defaultMaximumKeyInPrice,
                undefined,
                hasAvailableProducts,
                this.state.productPromotions?.FreeItemProductIds
            ),
            productComparisonButton: ArrayExtensions.hasElements(productComparisonButton)
                ? getBuyboxProductComparisonButton(
                      productComparisonButton[0],
                      product,
                      getCatalogId(this.props.context.request),
                      this.state
                  )
                : undefined,
            addToOrderTemplate: getBuyboxProductAddToOrderTemplate(props, this.state, this.buyboxCallbacks),
            addToWishlist: getBuyboxProductAddToWishlist(props, this.state, this.buyboxCallbacks),
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(props),
            quantity: product.IsGiftCard || hasSerialNumber ? undefined : getBuyboxProductQuantity(props, this.state, this.buyboxCallbacks),
            inventoryLabel: getBuyBoxInventoryLabel(props),
            shopSimilarLook:
                this.props.config.enableShopSimilarLooks && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Looks)
                    : undefined,
            shopSimilarDescription:
                this.props.config.enableShopSimilarDescription && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description)
                    : undefined,
            keyInPrice:
                this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(props, this.state, this.buyboxCallbacks)
                    : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(props, this.state),
            max,
            catalogs: this._validateCatalogDetails(catalogId)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        window.scrollTo(boundingRect.left, boundingRect.top);
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }

    private _updatePrice(newPrice: ProductPrice | undefined, customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    private _getProductVariant(dimensionsToUpdate: { [id: number]: string }) {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.allDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(
                product!.MasterProductId ? product!.MasterProductId : product!.RecordId,
                channelId,
                undefined,
                undefined,
                this.props.context.request
            ),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        return { productVariantPromise: selectedProductPromise, mappedDimensions };
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const product = this.props.data.product.result;

        const productDimensions = this.props.data.allDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true, isUpdatingDeliveryOptions: true });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[key]) {
                this.setState(previousState => {
                    const errorState = previousState.errorState;
                    errorState.configureErrors[key] = undefined;
                    return { errorState };
                });
            }
        }

        const data = this._getProductVariant(dimensionsToUpdate);
        const productVariant: SimpleProduct | null = await data.productVariantPromise;
        process.env.NODE_ENV === 'development' && console.log('Buybox', 'productVariant', JSON.parse(JSON.stringify(productVariant)));

        if (!productVariant) {
            return;
        }

        const promises: Promise<void>[] = [
            this._getDimensionsForSelectedVariant(productVariant, data.mappedDimensions),
            this._hydrateInventoryInfo(productVariant),
            this._hydratePrice(productVariant),
            this._hydrateDeliveryOptions(productVariant),
            this._updateQuantitiesInState(productVariant),
            this._updateAvailableForPickup(productVariant),
            this._updatePromotions(productVariant),
            this._updateWarranties(productVariant),
            this._updateSelectedProduct(productVariant),
            this._mediaLocations(productVariant)
        ];

        await Promise.all(promises);
    };

    private async _getDimensionsForSelectedVariant(
        productVariant: SimpleProduct,
        mappedDimensions: {
            /* eslint-disable @typescript-eslint/naming-convention -- Retail proxy values */
            DimensionTypeValue: number;
            DimensionValue: ProductDimensionValue | undefined;
            ExtensionProperties: CommerceProperty[] | undefined;
            /* eslint-enable @typescript-eslint/naming-convention -- Retail proxy values */
        }[]
    ): Promise<void> {
        await getDimensionsForSelectedVariant(
            new GetDimensionsForSelectedVariantInput(
                productVariant.MasterProductId ? productVariant.MasterProductId : productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                mappedDimensions,
                this.props.context.request
            ),
            this.props.context.actionContext
        );
    }

    private async _hydrateInventoryInfo(productVariant: SimpleProduct): Promise<void> {
        // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
        // so that places like add to cart can await it
        const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        const newShippingQuantity = newAvailableQuantity?.find(
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (shipping: { deliveryType: any }) => shipping.deliveryType === DeliveryMode.shipping
        );
        const isCustomPriceSelected = productVariant.Dimensions?.find(
            dimension =>
                dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value?.toLowerCase() === 'custom'
        );
        if (isCustomPriceSelected) {
            this.setState({ isCustomPriceSelected: true });
        } else {
            this.setState(previousState => {
                const errorState = previousState.errorState;
                errorState.customAmountError = undefined;
                return { isCustomPriceSelected: false, isPriceKeyedIn: false, errorState };
            });
        }

        if (newShippingQuantity) {
            this.setState({ productAvailableQuantity: newShippingQuantity });
        } else {
            this.setState({ productAvailableQuantity: undefined });
        }
    }

    private async _hydratePrice(productVariant: SimpleProduct): Promise<void> {
        // Step 6. Use these dimensions hydrate the product price.
        const newPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        if (newPrice) {
            this._updatePrice(newPrice);
        }
    }

    private async _hydrateDeliveryOptions(productVariant: SimpleProduct): Promise<void> {
        const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );

        // Step 7. Use these dimensions hydrate the product delivery options.
        const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                undefined,
                undefined,
                retailMultiplePickupFeatureState?.IsEnabled
            ),
            this.props.context.actionContext
        );

        if (newDeliveryOptions) {
            this.setState({ productDeliveryOptions: newDeliveryOptions });
        }
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
        if (isOrderQuantityLimitsFeatureEnabled && product) {
            this.setState({
                min: product.Behavior?.MinimumQuantity || 1,

                // If max by feature in default order settings is not defined then use max from site settings or default max 10.
                max:
                    product.Behavior?.MaximumQuantity && product.Behavior?.MaximumQuantity > 0
                        ? product.Behavior?.MaximumQuantity
                        : updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem)
            });
        } else {
            this.setState({
                min: 1,
                max: updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem)
            });
        }
    }

    private readonly _getDropdownName = (dimensionType: number, resources: IHeiBuyboxV2Resources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        const leafCategories = this.props.data.product.result?.ExtensionProperties?.find(p => p.Key === 'LeafCategories')?.Value
            ?.StringValue;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                if (leafCategories) {
                    for (const leafCategory of leafCategories.split('|')) {
                        if (this.mountCategories[leafCategory]) {
                            return resources.productDimensionTypeMount;
                        }
                    }
                }

                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                if (leafCategories) {
                    for (const leafCategory of leafCategories.split('|')) {
                        if (this.packSizeCategories[leafCategory]) {
                            return resources.productDimensionTypePackSize;
                        }
                    }
                }

                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        const featureStatuses = await this.props.data.featureState;
        const isFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
        )?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        let customerInfo;
        try {
            customerInfo = await this.props.data.customerInformation;
        } catch (error) {
            this.props.telemetry.information(`${error}`);
            this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
            return false;
        }

        return (
            customerInfo &&
            ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
        );
    }

    private _validateCatalogDetails(catalogId: number): ProductCatalog[] | undefined {
        const catalogs = this.props.data.catalogs?.result?.filter(item => item.RecordId === catalogId);
        if (ArrayExtensions.hasElements(catalogs) && catalogs[0].RecordId === 0) {
            return;
        }

        return catalogs;
    }

    private async _updateAvailableForPickup(productVariant: SimpleProduct) {
        const ctx = this.props.context.actionContext;
        const searchCriteria = createInventoryAvailabilitySearchCriteria(
            ctx,
            [productVariant.RecordId],
            false,
            true,
            {},
            DeliveryMode.pickup
        );

        const productInventoryInformation = await ProductsDataActions.getEstimatedAvailabilityAsync(
            { callerContext: ctx },
            searchCriteria
        ).then(async (productWarehouseInformation: ProductWarehouseInventoryInformation) => {
            // For store selector, inventory should always come from an individual store
            const productInventoryInformation = mapProductInventoryInformation(
                ctx,
                productWarehouseInformation.ProductWarehouseInventoryAvailabilities
            );

            process.env.NODE_ENV === 'development' && console.log('getFullAvailableInventoryNearbyAction', productInventoryInformation);

            return productInventoryInformation;
        });

        // Ensure inventory data is for the selected variant
        if (productVariant.RecordId === this.state.productVariantData?.RecordId) {
            const storeAvailabilities = productInventoryInformation?.filter(
                a => a.InventLocationId !== '099' && (a.ProductAvailableQuantity.AvailableQuantity ?? 0) > 0
            );

            process.env.NODE_ENV === 'development' &&
                console.log(
                    'Buybox',
                    'storeAvailabilities',
                    productVariant.RecordId,
                    JSON.parse(JSON.stringify(storeAvailabilities)),
                    JSON.parse(JSON.stringify(productInventoryInformation))
                );

            this.setState({ storeAvailabilities: storeAvailabilities });
        }
    }

    private async _updatePromotions(productVariant: SimpleProduct) {
        const product = this.props.data.product.result;

        if (product) {
            const promotions = await getProductPromotionsAction(
                new GetProductPromotionsInput(product.RecordId, productVariant.Dimensions ?? []),
                this.props.context.actionContext
            );

            process.env.NODE_ENV === 'development' && console.log('Buybox', 'productPromotions', promotions);
            this.setState({ productPromotions: promotions });

            const productPromotionsChangeEvent = new CustomEvent<IGetProductPromotionResponse>('onProductPromotionsChange', {
                detail: promotions
            });

            window.dispatchEvent(productPromotionsChangeEvent);
        }
    }

    private async _updateWarranties(productVariant: SimpleProduct) {
        const product = this.props.data.product.result;

        if (product) {
            const applicableWarranties = await getApplicableWarrantiesAction(
                new getApplicableWarrantiesInput(product.RecordId, productVariant.Dimensions ?? []),
                this.props.context.actionContext
            );

            let selectedWarranty = this.state.selectedWarranty;

            if (!ArrayExtensions.hasElements(applicableWarranties)) {
                // no applicable warranties for the selected variant
                selectedWarranty = null;
            } else if (selectedWarranty) {
                // different set of warranties for the selected variant
                if (!Boolean(applicableWarranties[0].Warranties?.find(aw => aw.RecordId === selectedWarranty!.RecordId))) {
                    selectedWarranty = null;
                }
            }

            this.setState({ applicableWarranties: applicableWarranties, selectedWarranty: selectedWarranty });
        }
    }

    private async _mediaLocations(productVariant: SimpleProduct) {
        const input = new MediaLocationsForSelectedVariantInput(
            productVariant.RecordId,
            this.props.context.actionContext.requestContext.apiSettings.channelId,
            productVariant,
            this.props.context.actionContext.requestContext.apiSettings.catalogId
        );

        const mediaLocations = await getMediaLocationsForSelectedVariantActionDataAction(input, this.props.context.actionContext);
        process.env.NODE_ENV === 'development' && console.log('_mediaLocations', JSON.parse(JSON.stringify(mediaLocations)));
        this.setState({ mediaLocations: mediaLocations ?? [] });
    }

    private async _updateSelectedProduct(productVariant: SimpleProduct) {
        this.setState({ productVariantData: productVariant });
    }
}

export default Buybox;
